
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UserRole } from "@/types/auth";
import { Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";

type ProtectedRouteProps = {
  children: React.ReactNode;
  requiredRoles?: UserRole | UserRole[];
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
}) => {
  const location = useLocation();

  // Хуки должны вызываться безусловно согласно правилам React
  const { user, profile, isAuthenticated, hasPermission, isLoading } = useAuth();

  // Debug logging removed for production performance

  // Show loading state while checking authentication
  if (isLoading) {
    // Loading state - debug log removed
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Загрузка...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Redirecting to login - debug log removed
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // If we have a user but no profile yet, show loading
  if (!profile) {
    // Profile loading state - debug log removed
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Загрузка профиля...</p>
        </div>
      </div>
    );
  }

  // Check if user has required role(s)
  if (requiredRoles && !hasPermission(requiredRoles)) {
    return <Navigate to="/403" replace />;
  }

  // All checks passed - debug log removed
  
  // User is authenticated and has required role(s) - wrap in Layout
  return <Layout>{children}</Layout>;
};

export default ProtectedRoute;
